import {AxiosError, AxiosResponse} from 'axios';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';

import {ClientDetails, ClientInvoiceAddress} from '@store/clients/details/client-details.model';
import {
  getClientDetailsDataSource,
  putClientFacturationModeDataSource
} from '@store/clients/details/client-details.requests';
import {clientDetailsStore} from '@store/clients/details/client-details.store';
import {ManageContact, ManageOrganisation} from '@store/common/organisation.model';

import APIAxios, {APIRoutes} from '@api/axios.api';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import {formatFacturationModeToUpdateDto} from '@store/clients/details/facturationMode/facturation-mode.utils';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import SnackError from '@utils/error.utils';

export class ClientDetailsService {
  store = clientDetailsStore;

  resetStore = () => this.store.reset();
  resetDocuments = () => this.store.update((state) => ({
    ...state,
    documents: [],
  }));

  getClientById = (clientId: number | string): Observable<ClientDetails> => {
    return from(APIAxios(APIRoutes.GETClientById(clientId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientDetails>) => {
          return {
            ...response.data,
            selectedBillingManager: response.data.billingManager ? {
              value: response.data.billingManager.id,
              label: [response.data.billingManager.firstName, response.data.billingManager.lastName].join(' ')
            } : undefined
          };
        }),
        tap((client) =>
          this.store.update((state) => ({
              ...state,
              ...client,
            }),
            getClientDetailsDataSource.setSuccess(),
          )
        ),
        getClientDetailsDataSource.trackRequestStatus(),
      );
  };

  updateClientStatus = (clientId: number | string, status: GlobalStatus): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.PATCHClientStatus(clientId),
      data: {status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap(() => {
          this.store.update((state) => ({
            ...state,
            status,
          }));
        }),
      );
  };

  updateClient = (clientId: number | string, data: ManageOrganisation): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientById(clientId),
      data: {
        ...data,
        inBillingToOrganisationId: data.inBillingToOrganisation?.id,
        billingManagerId: data.selectedBillingManager?.value,
        email: data.email != '' ? data.email : undefined,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisation>) => {
          return response.data;
        }),
        switchMap(() => this.getClientById(clientId)),
      );
  };

  deleteClient = (clientId: number | string): Observable<AxiosResponse> => {
    return from(APIAxios(APIRoutes.DELETEClient(clientId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createClientContact = (clientId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTClientContact(clientId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientById(clientId)),
      );
  };

  getContacts = (clientId: number | string) => {
    return from(APIAxios(APIRoutes.GETClientContact(clientId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        })
      );
  };

  updateClientContact = (clientId: number | string, contactId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientContactById(clientId, contactId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientById(clientId)),
      );
  };

  deleteClientContact = (clientId: number | string, contactId: number | string): Observable<ManageOrganisation> => {
    return from(APIAxios(APIRoutes.DELETEClientContact(clientId, contactId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClientById(clientId)),
      );
  };

  updateClientFacturationMode = (clientId: number | string, facturationModeId: number | string, existingData: FacturationMode, data: FacturationMode): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTClientFacturationModeById(clientId, facturationModeId),
      data: formatFacturationModeToUpdateDto(existingData, data),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<FacturationMode>) => response.data),
        tap(() =>
          this.store.update((state) => ({
              ...state,
              facturationMode: data,
            }),
            putClientFacturationModeDataSource.setSuccess(),
          )
        ),
        putClientFacturationModeDataSource.trackRequestStatus(),
      );
  };

  getInvoiceAddresses(clientId: number | string): Observable<ClientInvoiceAddress[]> {
    return from(APIAxios({
      ...APIRoutes.GETClientInvoiceAddresses(clientId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientInvoiceAddress[]>) => response.data),
        tap((resp) => {
          this.store.update((state) => ({
            ...state,
            clientInvoiceAddresses: resp,
          }));
        })
      );
  }

  updateInvoiveAddress(clientInvoiceAddressId: number, emails: string[], ccEmails: string[], clientId: number | string) {
    return from(APIAxios({
      ...APIRoutes.PUTInvoiceAddress(),
      data: {
        clientInvoiceAddressId,
        emails,
        ccEmails,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getInvoiceAddresses(clientId)),
      );
  }
}

export const clientDetailsService = new ClientDetailsService();
